import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Button, Grid, Paper } from "@mui/material";
import { Helmet } from "react-helmet-async";

import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import PlaylistAddCheckCircleRoundedIcon from "@mui/icons-material/PlaylistAddCheckCircleRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";

import Logo from "../../assets/Logo.png";
import RSS from "../../assets/rosoftsavvy.png";
import { signin } from "../../actions/auth";
import TwoFactorStep from "../../twoFactor/TwoFactorStep";
import Input from "./input";
import {
  BANK_NAME,
  IDLE_SIGNED_OUT_KEY,
  SUITE_NAME,
  SUITE_TAGLINE,
  VENDOR_NAME,
  pageTitle,
} from "../../brand";

import "./Login.css";

const backgroundBars = [28, 43, 36, 58, 48, 70, 57, 82, 66, 91, 78, 100];

// The systems a user can reach from here, shown around the sign-in card
const suiteModules = [
  {
    title: "FinSight",
    description: "Financial reporting",
    Icon: AssessmentRoundedIcon,
    position: "suite-module-card-left-one",
  },
  {
    title: "HR",
    description: "People and payroll",
    Icon: GroupsRoundedIcon,
    position: "suite-module-card-right-one",
  },
  {
    title: "Assets Register",
    description: "Fixed assets",
    Icon: Inventory2RoundedIcon,
    position: "suite-module-card-left-two",
  },
  {
    title: "Stationery Stock",
    description: "Inventory control",
    Icon: WarehouseRoundedIcon,
    position: "suite-module-card-right-two",
  },
  {
    title: "Reconciliation",
    description: "Account matching",
    Icon: PlaylistAddCheckCircleRoundedIcon,
    position: "suite-module-card-left-three",
  },
  {
    title: "Portfolio Manager",
    description: "Investments",
    Icon: ShowChartRoundedIcon,
    position: "suite-module-card-right-three",
  },
  {
    title: "Risk Manager",
    description: "Insurance and risk",
    Icon: ShieldRoundedIcon,
    position: "suite-module-card-left-four",
  },
  {
    title: "eClaim",
    description: "Expense claims",
    Icon: PaidRoundedIcon,
    position: "suite-module-card-right-four",
  },
];

const Login = () => {
  const initialState = {
    email: "",
    password: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Set when the password was right but the authenticator code is still due
  const [twoFactor, setTwoFactor] = useState(null);

  // Signed out by the idle timer? Say so once.
  const [idleNotice, setIdleNotice] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem(IDLE_SIGNED_OUT_KEY)) {
        sessionStorage.removeItem(IDLE_SIGNED_OUT_KEY);
        setIdleNotice(true);
      }
    } catch (error) {
      // storage blocked: nothing to show
    }
  }, []);


  const handleOnSubmit = (event) => {
    event.preventDefault();
    dispatch(signin(formData, navigate)).then((pending) => {
      if (pending?.twoFactor) setTwoFactor(pending);
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previousData) => ({
      ...previousData,
      [name]: value,
    }));
  };

  const handleShowPassword = () => {
    setShowPassword((previousValue) => !previousValue);
  };

  return (
    <div className="suite-login-page">
      <Helmet>
        <title>{pageTitle("Sign in")}</title>
      </Helmet>

      <div className="suite-background-grid" aria-hidden="true" />

      {/* Main financial trend background */}
      <div className="suite-financial-chart" aria-hidden="true">
        <svg viewBox="0 0 1500 600" preserveAspectRatio="none">
          <defs>
            <linearGradient id="suiteAreaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />

              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </linearGradient>

            <linearGradient
              id="suiteGreenAreaGradient"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.2" />

              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </linearGradient>

            <filter id="suiteBlueGlow">
              <feGaussianBlur stdDeviation="5" result="blur" />

              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <filter id="suiteGreenGlow">
              <feGaussianBlur stdDeviation="4" result="blur" />

              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Horizontal chart lines */}
          {[80, 170, 260, 350, 440, 530].map((position) => (
            <line
              key={position}
              x1="0"
              y1={position}
              x2="1500"
              y2={position}
              stroke="#cbd5e1"
              strokeWidth="1"
              opacity="0.09"
            />
          ))}

          {/* Vertical chart lines */}
          {[100, 250, 400, 550, 700, 850, 1000, 1150, 1300, 1450].map(
            (position) => (
              <line
                key={position}
                x1={position}
                y1="0"
                x2={position}
                y2="600"
                stroke="#3b82f6"
                strokeWidth="1"
                opacity="0.07"
              />
            ),
          )}

          {/* Blue area */}
          <polygon
            points="
              0,515
              110,480
              220,495
              330,430
              440,450
              550,360
              660,390
              770,290
              880,325
              990,225
              1100,260
              1210,155
              1320,190
              1420,85
              1500,40
              1500,600
              0,600
            "
            fill="url(#suiteAreaGradient)"
          />

          {/* Green area */}
          <polygon
            points="
              0,560
              120,545
              240,530
              360,510
              480,480
              600,450
              720,420
              840,385
              960,350
              1080,315
              1200,275
              1320,230
              1420,185
              1500,145
              1500,600
              0,600
            "
            fill="url(#suiteGreenAreaGradient)"
          />

          {/* Budget line */}
          <polyline
            points="
              0,540
              110,525
              220,510
              330,490
              440,470
              550,440
              660,410
              770,380
              880,345
              990,310
              1100,270
              1210,230
              1320,185
              1420,135
              1500,100
            "
            fill="none"
            stroke="#cbd5e1"
            strokeWidth="3"
            strokeDasharray="14 14"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.38"
          />

          {/* Green performance line */}
          <polyline
            points="
              0,560
              120,545
              240,530
              360,510
              480,480
              600,450
              720,420
              840,385
              960,350
              1080,315
              1200,275
              1320,230
              1420,185
              1500,145
            "
            fill="none"
            stroke="#10b981"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#suiteGreenGlow)"
            opacity="0.72"
          />

          {/* Main actual line */}
          <polyline
            points="
              0,515
              110,480
              220,495
              330,430
              440,450
              550,360
              660,390
              770,290
              880,325
              990,225
              1100,260
              1210,155
              1320,190
              1420,85
              1500,40
            "
            fill="none"
            stroke="#3b82f6"
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#suiteBlueGlow)"
          />

          {[
            [330, 430],
            [550, 360],
            [770, 290],
            [990, 225],
            [1210, 155],
            [1420, 85],
            [1500, 40],
          ].map(([cx, cy]) => (
            <g key={`${cx}-${cy}`}>
              <circle cx={cx} cy={cy} r="12" fill="#3b82f6" opacity="0.12" />

              <circle cx={cx} cy={cy} r="5" fill="#3b82f6" />
            </g>
          ))}
        </svg>
      </div>

      {/* Background bar chart */}
      <div className="suite-background-bars" aria-hidden="true">
        {backgroundBars.map((height, index) => (
          <div
            key={`${height}-${index}`}
            className="suite-background-bar-wrapper"
          >
            <span
              className={`suite-background-bar ${
                index === backgroundBars.length - 1
                  ? "suite-background-bar-active"
                  : ""
              }`}
              style={{ height: `${height}%` }}
            />
          </div>
        ))}
      </div>

      {/* Available software modules */}
      <div className="suite-module-showcase" aria-hidden="true">
        {suiteModules.map(({ title, description, Icon, position }) => (
          <div key={title} className={`suite-module-card ${position}`}>
            <span className="suite-module-icon">
              <Icon />
            </span>

            <span className="suite-module-copy">
              <strong>{title}</strong>
              <small>{description}</small>
            </span>
          </div>
        ))}
      </div>

      <header className="suite-header">
        <div className="cbx">
          <h1 className="cbx-title-2">CBX</h1>

          <p className="cbx-subtitle">{SUITE_TAGLINE}</p>
        </div>
      </header>

      <main className="suite-login-content">
        <Paper elevation={0} className="suite-login-card">
          <div className="suite-card-top-line" />

          <div className="suite-bank-brand">
            <div className="suite-bank-logo">
              <img src={Logo} alt={BANK_NAME} />
            </div>

            <div className="suite-bank-details">
              <span>{BANK_NAME.toUpperCase()}</span>

              <small>Integrated Software Access Portal</small>
            </div>
          </div>

          <div className="suite-login-heading">
            <span className="suite-secure-label">SECURE SUITE ACCESS</span>

            <h2>Sign in to {SUITE_NAME}</h2>

            <p>
              One sign-in for the bank&apos;s internal systems you have access
              to.
            </p>
            {idleNotice && (
              <p className="suite-idle-notice" role="status">
                You were signed out because there was no activity for 15
                minutes.
              </p>
            )}
          </div>

          <div className="suite-access-features">
            <div>
              <span className="suite-feature-icon">✓</span>
              <span>Single access point</span>
            </div>

            <div>
              <span className="suite-feature-icon">✓</span>
              <span>Secure authentication</span>
            </div>

            <div>
              <span className="suite-feature-icon">✓</span>
              <span>Role-based software access</span>
            </div>
          </div>

          {twoFactor ? (
            <TwoFactorStep
              step={twoFactor.twoFactor}
              tempToken={twoFactor.tempToken}
              onDone={(data) => dispatch(signin(null, navigate, data))}
              onCancel={() => setTwoFactor(null)}
            />
          ) : (
          <form className="suite-login-form" onSubmit={handleOnSubmit}>
            <Grid container spacing={2}>
              <Input
                autoFocus
                name="email"
                placeholder="Enter your email"
                handleChange={handleChange}
                type="email"
                style={{
                  backgroundColor: "rgba(15, 23, 42, 0.96)",
                  border: "1px solid #64748b",
                  borderRadius: "10px",
                  color: "#e5e7eb",
                }}
              />

              <Input
                name="password"
                placeholder="Enter your password"
                handleChange={handleChange}
                type={showPassword ? "text" : "password"}
                handleShowPassword={handleShowPassword}
                style={{
                  backgroundColor: "rgba(15, 23, 42, 0.96)",
                  border: "1px solid #64748b",
                  borderRadius: "10px",
                  color: "#e5e7eb",
                }}
              />
            </Grid>

            <p className="suite-privacy-message">
                For authorised {BANK_NAME} staff only. Sign-ins and activity
                are logged and may be audited. Never share your password or
                authenticator codes.
              </p>

            <Button
              disableElevation
              className="suite-submit-button"
              type="submit"
              id="button"
            >
              Sign in
            </Button>

            <div className="suite-forgot-password">
              <Link to="/resetPassword">Forgot your password?</Link>
            </div>

            <div className="suite-powered-by">
              <em>Powered by</em>

              <img src={RSS} alt={VENDOR_NAME} />
            </div>
          </form>
          )}
        </Paper>

        <div className="suite-security-message">
          <span className="suite-security-dot" />

          <span>Protected enterprise access</span>
        </div>
      </main>
    </div>
  );
};

export default Login;
